import React, { useState, useEffect } from "react";
import PagesHeader from "../../components/PageHeader";
import ButtonBar from "../../components/ButtonNavigation/index";
import * as InstallerData from "../../services/InstallerData";
import * as GetUserDataService from "../../services/GetUserData";
import * as TrustedDealerService from "../../services/TrustedDealer";
import * as SubscriptionService from "../../services/InstallerSubscriptionService";
import SeatGatekeeperModal from "../../components/InstallerSubscription/SeatGatekeeperModal";
import { useDispatch } from "react-redux";
import {
  Row,
  Col,
  Form,
  Card,
  Switch,
  Input,
  Radio,
  Button,
  Tag,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  BankOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const AddInstaller = () => {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showHideCompany, setShowHideCompany] = useState(true);
  const [showHideAddress, setShowHideAddress] = useState(true);
  const [form] = Form.useForm();

  const [gatekeeperType, setGatekeeperType] = useState(null);
  const [showGatekeeperModal, setShowGatekeeperModal] = useState(false);
  const [seatData, setSeatData] = useState(null);

  useEffect(() => {
    checkSeatsAndFetchData();
    window.scrollTo(0, 0);
  }, []);

  const checkSeatsAndFetchData = async () => {
    try {
      const installersRes = await dispatch(InstallerData.getInstallerListing());
      const usedCount = Array.isArray(installersRes) ? installersRes.length : 0;
      const res = await dispatch(SubscriptionService.getInstallerSeatStatus(usedCount));
      setSeatData(res);

      const totalSeats = Number(res?.seats?.total_seats ?? 0);
      const usedSeats = Number(res?.seats?.used_seats ?? usedCount);
      const remainingSeats = res?.seats?.remaining_seats !== undefined 
        ? Number(res.seats.remaining_seats) 
        : Math.max(0, totalSeats - usedSeats);
      const isSubscribed = Boolean(res?.is_subscribed || totalSeats > 0);

      if (!isSubscribed) {
        setGatekeeperType("not_subscribed");
        setShowGatekeeperModal(true);
        return;
      }

      if (remainingSeats <= 0) {
        setGatekeeperType("limit_reached");
        setShowGatekeeperModal(true);
        return;
      }

      fetchDealerData();
    } catch (err) {
      fetchDealerData();
    }
  };

  const parseAddress = (fullAddress) => {
    if (!fullAddress) return { street: "", city: "", state: "", zip: "", country: "USA" };
    
    const parts = fullAddress.split(",").map((p) => p.trim());
    
    let street = parts[0] || "";
    let city = "";
    let state = "";
    let zip = "";
    let country = "USA";

    if (parts.length >= 4) {
      street = parts[0];
      city = parts[1];
      state = parts[2];
      zip = parts[3];
      if (parts[4]) country = parts[4];
    } else if (parts.length === 3) {
      street = parts[0];
      city = parts[1];
      const stateZip = parts[2].trim().split(" ");
      state = stateZip[0] || "";
      zip = stateZip[1] || "";
    } else if (parts.length === 2) {
      street = parts[0];
      city = parts[1];
    }

    return { street, city, state, zip, country };
  };

  const fetchDealerData = () => {
    dispatch(GetUserDataService.getUserData())
      .then((userRes) => {
        const pId = userRes?.inspector_id || (userRes?.profiles && userRes?.profiles[0]?.ID) || 0;
        
        if (pId) {
          dispatch(TrustedDealerService.getTrustedDealerProfileData(pId))
            .then((profileRes) => {
              const pData = (profileRes && profileRes.data) ? profileRes.data : profileRes;
              populateForm(userRes, pData);
            })
            .catch(() => {
              populateForm(userRes, null);
            });
        } else {
          populateForm(userRes, null);
        }
      })
      .catch((err) => {
        console.info("Error fetching user data...", err);
      });
  };

  const populateForm = (userRes, profileRes) => {
    setShowHideCompany(true);
    setShowHideAddress(true);

    const compName = profileRes?.organization_name || userRes?.company_name || userRes?.display_name || "";
    const rawAddress = profileRes?.address || userRes?.street || userRes?.street_address || "";
    const parsed = parseAddress(rawAddress);

    const streetStr = parsed.street || rawAddress;
    const cityStr = userRes?.city || parsed.city || "";
    const stateStr = userRes?.state || userRes?.states || parsed.state || "";
    const countryStr = userRes?.country || parsed.country || "USA";
    const zipStr = userRes?.zip_code || userRes?.zipcode || parsed.zip || "";

    const phoneStr = profileRes?.phone_number || userRes?.phone || userRes?.phone_personal || "";
    const emailStr = profileRes?.email || userRes?.email_address || userRes?.email || "";
    const websiteStr = profileRes?.website || userRes?.website || "";
    const compTypeStr = userRes?.company_type || "";

    form.setFieldsValue({
      comp_name: compName,
      street: streetStr,
      phone: phoneStr,
      comp_email: emailStr,
      website: websiteStr,
      city: cityStr,
      state: stateStr,
      country: countryStr,
      zip_code: zipStr,
      comp_type: compTypeStr,
    });
  };

  const toggleCompany = (checked) => {
    setShowHideCompany(checked);
  };

  const toggleAddress = (checked) => {
    setShowHideAddress(checked);
  };

  const onFinish = (values) => {
    setLoading(true);
    values.company_info = showHideCompany ? "enable" : "disable";
    values.shipping_info = showHideAddress ? "enable" : "disable";
    values.welcome_email = values.welcome_email === "yes" ? "yes" : "no";

    dispatch(InstallerData.addInstallerData(values))
      .then((res) => {
        setLoading(false);
        Swal.fire({
          icon: "success",
          title: "Installer Registered!",
          text: "New Installer team member added successfully.",
          confirmButtonColor: "#0a497b",
        }).then(() => {
          navigate("/single-installer/?user_id=" + res.user_id);
        });
      })
      .catch((err) => {
        setLoading(false);
        let errorMessage = "An error occurred while creating Installer.";
        if (err.response && err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (typeof err === "string") {
          errorMessage = err;
        }
        Swal.fire({
          icon: "error",
          title: "Registration Error",
          text: errorMessage,
          confirmButtonColor: "#0a497b",
        });
      });
  };

  // Custom inline styles
  const cardStyle = {
    borderRadius: "16px",
    border: "1px solid #cbd5e1",
    boxShadow: "0 10px 25px rgba(10, 73, 123, 0.05)",
    marginBottom: "24px",
    background: "#ffffff",
    overflow: "hidden",
  };

  const headerBadgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #0a497b 0%, #0284c7 100%)",
    color: "#ffffff",
    fontSize: "18px",
    marginRight: "12px",
    boxShadow: "0 4px 12px rgba(2, 132, 199, 0.25)",
  };

  const inputStyle = {
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    boxShadow: "none",
  };

  return (
    <div className="inspectionWrapper" style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="inspectionHeader">
        <PagesHeader title="Add New Installer" type="secondary" />
      </div>
      <div className="buttonSection" style={{ position: "relative", zIndex: 100 }}>
        <div className="container-xxl">
          <Row align="middle" justify="space-between">
            <Col xs={24} md={16} className="text-end">
              <ButtonBar />
            </Col>
            <Col xs={24} md={8} className="text-end">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/all-installers")}
                style={{
                  borderRadius: "20px",
                  fontWeight: 600,
                  borderColor: "#cbd5e1",
                  color: "#475569",
                }}
              >
                Back to Listing
              </Button>
            </Col>
          </Row>
        </div>
      </div>

      <div className="inspectionWrapInner mt-3">
        <div className="container-xxl" style={{ maxWidth: "1100px" }}>
          
          {/* Top Banner Card */}
          <div
            style={{
              background: "linear-gradient(135deg, #0a497b 0%, #0284c7 100%)",
              borderRadius: "16px",
              padding: "24px 32px",
              color: "#ffffff",
              marginBottom: "24px",
              boxShadow: "0 12px 28px rgba(2, 132, 199, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                <Tag color="#ffffff" style={{ color: "#0a497b", fontWeight: 700, borderRadius: "12px", padding: "2px 12px" }}>
                  INSTALLER MANAGEMENT
                </Tag>
                <Tag color="rgba(255,255,255,0.2)" style={{ color: "#ffffff", borderRadius: "12px" }}>
                  Role: um_installer
                </Tag>
              </div>
              <h3 style={{ color: "#ffffff", margin: 0, fontWeight: 700, fontSize: "22px" }}>
                Create Installer Sub-Account
              </h3>
              <p style={{ margin: 0, color: "rgba(255,255,255,0.85)", fontSize: "14px", marginTop: "4px" }}>
                Add a new installer under your Trusted Dealer profile. Credentials will be securely generated and sent via email.
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <SafetyCertificateOutlined style={{ fontSize: "42px", color: "rgba(255,255,255,0.3)" }} />
            </div>
          </div>

          <Form
            form={form}
            name="add_installer"
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              welcome_email: "yes",
            }}
          >
            {/* Account Credentials Card */}
            <Card
              style={cardStyle}
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={headerBadgeStyle}>
                    <LockOutlined />
                  </span>
                  <div>
                    <h5 style={{ margin: 0, fontWeight: 700, color: "#0f172a", fontSize: "16px" }}>
                      Account Credentials & Access
                    </h5>
                    <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 400 }}>
                      Set up login email and password
                    </span>
                  </div>
                </div>
              }
            >
              <Row gutter={20}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="email_add"
                    label={<span style={{ fontWeight: 600, color: "#334155" }}>Email Address (Login ID)</span>}
                    rules={[
                      { required: true, message: "Please enter email address!" },
                      { type: "email", message: "Please enter a valid email address!" },
                    ]}
                  >
                    <Input
                      className="formControl"
                      prefix={<MailOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="installer@example.com"
                      size="large"
                      style={inputStyle}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="pwd"
                    label={<span style={{ fontWeight: 600, color: "#334155" }}>Password</span>}
                    rules={[{ required: true, message: "Please enter password!" }]}
                  >
                    <Input.Password
                      className="formControl"
                      prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Create secure password"
                      size="large"
                      style={inputStyle}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="confirm_pass"
                    label={<span style={{ fontWeight: 600, color: "#334155" }}>Confirm Password</span>}
                    dependencies={["pwd"]}
                    rules={[
                      { required: true, message: "Please confirm password!" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("pwd") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("Passwords do not match!"));
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      className="formControl"
                      prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Re-type password"
                      size="large"
                      style={inputStyle}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Personal Details Card */}
            <Card
              style={cardStyle}
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={headerBadgeStyle}>
                    <IdcardOutlined />
                  </span>
                  <div>
                    <h5 style={{ margin: 0, fontWeight: 700, color: "#0f172a", fontSize: "16px" }}>
                      Personal Profile Details
                    </h5>
                    <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 400 }}>
                      Installer's personal contact information
                    </span>
                  </div>
                </div>
              }
            >
              <Row gutter={20}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="first_name"
                    label={<span style={{ fontWeight: 600, color: "#334155" }}>First Name</span>}
                    rules={[{ required: true, message: "Please enter first name!" }]}
                  >
                    <Input
                      className="formControl"
                      placeholder="First Name"
                      size="large"
                      style={inputStyle}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="last_name"
                    label={<span style={{ fontWeight: 600, color: "#334155" }}>Last Name</span>}
                    rules={[{ required: true, message: "Please enter last name!" }]}
                  >
                    <Input
                      className="formControl"
                      placeholder="Last Name"
                      size="large"
                      style={inputStyle}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="phone_personal"
                    label={<span style={{ fontWeight: 600, color: "#334155" }}>Personal Phone</span>}
                  >
                    <Input
                      className="formControl"
                      prefix={<PhoneOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="(555) 000-0000"
                      size="large"
                      style={inputStyle}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="welcome_email"
                    label={<span style={{ fontWeight: 600, color: "#334155" }}>Dispatch Welcome Email</span>}
                  >
                    <Radio.Group size="large" buttonStyle="solid">
                      <Radio.Button value="yes" style={{ borderRadius: "8px 0 0 8px" }}>
                        <SendOutlined className="me-1" /> Send Login Credentials
                      </Radio.Button>
                      <Radio.Button value="no" style={{ borderRadius: "0 8px 8px 0" }}>
                        Do Not Send
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Company Information Card with Toggle */}
            <Card
              style={cardStyle}
              title={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={headerBadgeStyle}>
                      <BankOutlined />
                    </span>
                    <div>
                      <h5 style={{ margin: 0, fontWeight: 700, color: "#0f172a", fontSize: "16px" }}>
                        Company & Business Profile
                      </h5>
                      <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 400 }}>
                        Auto-filled from your Trusted Dealer organization
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#f1f5f9", padding: "6px 16px", borderRadius: "20px" }}>
                    <span style={{ fontWeight: 600, fontSize: "13px", color: "#475569" }}>
                      Enable Company Info
                    </span>
                    <Switch checked={showHideCompany} onChange={toggleCompany} />
                  </div>
                </div>
              }
            >
              {showHideCompany ? (
                <Row gutter={20}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="comp_name"
                      label={<span style={{ fontWeight: 600, color: "#334155" }}>Company / Organization Name</span>}
                    >
                      <Input
                        className="formControl"
                        prefix={<BankOutlined style={{ color: "#94a3b8" }} />}
                        placeholder="Company Name"
                        size="large"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="website"
                      label={<span style={{ fontWeight: 600, color: "#334155" }}>Website URL</span>}
                    >
                      <Input
                        className="formControl"
                        prefix={<GlobalOutlined style={{ color: "#94a3b8" }} />}
                        placeholder="https://example.com"
                        size="large"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="comp_email"
                      label={<span style={{ fontWeight: 600, color: "#334155" }}>Company Email</span>}
                      rules={[{ type: "email", message: "Please enter valid email!" }]}
                    >
                      <Input
                        className="formControl"
                        prefix={<MailOutlined style={{ color: "#94a3b8" }} />}
                        placeholder="info@company.com"
                        size="large"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="phone"
                      label={<span style={{ fontWeight: 600, color: "#334155" }}>Company Phone</span>}
                    >
                      <Input
                        className="formControl"
                        prefix={<PhoneOutlined style={{ color: "#94a3b8" }} />}
                        placeholder="Company Phone"
                        size="large"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="comp_type"
                      label={<span style={{ fontWeight: 600, color: "#334155" }}>Company Type</span>}
                    >
                      <Input
                        className="formControl"
                        placeholder="e.g. Installation Contractor"
                        size="large"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : (
                <div style={{ textAlign: "center", padding: "16px 0", color: "#94a3b8" }}>
                  Company information section is disabled. Turn on the switch above to configure.
                </div>
              )}
            </Card>

            {/* Mailing and Shipping Address Card with Toggle */}
            <Card
              style={cardStyle}
              title={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={headerBadgeStyle}>
                      <EnvironmentOutlined />
                    </span>
                    <div>
                      <h5 style={{ margin: 0, fontWeight: 700, color: "#0f172a", fontSize: "16px" }}>
                        Mailing & Service Location
                      </h5>
                      <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 400 }}>
                        Auto-parsed address details
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#f1f5f9", padding: "6px 16px", borderRadius: "20px" }}>
                    <span style={{ fontWeight: 600, fontSize: "13px", color: "#475569" }}>
                      Enable Address Info
                    </span>
                    <Switch checked={showHideAddress} onChange={toggleAddress} />
                  </div>
                </div>
              }
            >
              {showHideAddress ? (
                <Row gutter={20}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="street"
                      label={<span style={{ fontWeight: 600, color: "#334155" }}>Street Address</span>}
                    >
                      <Input
                        className="formControl"
                        prefix={<EnvironmentOutlined style={{ color: "#94a3b8" }} />}
                        placeholder="Street Address"
                        size="large"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="city"
                      label={<span style={{ fontWeight: 600, color: "#334155" }}>City</span>}
                    >
                      <Input
                        className="formControl"
                        placeholder="City"
                        size="large"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="state"
                      label={<span style={{ fontWeight: 600, color: "#334155" }}>State / Province</span>}
                    >
                      <Input
                        className="formControl"
                        placeholder="State"
                        size="large"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="country"
                      label={<span style={{ fontWeight: 600, color: "#334155" }}>Country</span>}
                    >
                      <Input
                        className="formControl"
                        placeholder="Country"
                        size="large"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="zip_code"
                      label={<span style={{ fontWeight: 600, color: "#334155" }}>Zip Code</span>}
                    >
                      <Input
                        className="formControl"
                        placeholder="Zip Code"
                        size="large"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : (
                <div style={{ textAlign: "center", padding: "16px 0", color: "#94a3b8" }}>
                  Mailing address section is disabled. Turn on the switch above to configure.
                </div>
              )}
            </Card>

            {/* Bottom Floating Action Bar */}
            <div
              style={{
                background: "#ffffff",
                padding: "16px 24px",
                borderRadius: "16px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                border: "1px solid #cbd5e1",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                size="large"
                onClick={() => navigate("/all-installers")}
                style={{
                  borderRadius: "10px",
                  fontWeight: 600,
                  padding: "0 24px",
                }}
              >
                Cancel
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<CheckCircleOutlined />}
                size="large"
                style={{
                  borderRadius: "10px",
                  fontWeight: 700,
                  padding: "0 32px",
                  background: "linear-gradient(135deg, #0a497b 0%, #0284c7 100%)",
                  borderColor: "transparent",
                  boxShadow: "0 4px 16px rgba(2, 132, 199, 0.35)",
                }}
              >
                Save Installer Profile
              </Button>
            </div>
          </Form>
        </div>
      </div>

      {/* Luxury Seat Gatekeeper Modal */}
      <SeatGatekeeperModal
        visible={showGatekeeperModal}
        onClose={() => {
          setShowGatekeeperModal(false);
          navigate("/all-installers");
        }}
        type={gatekeeperType}
        seatData={seatData}
      />
    </div>
  );
};

export default AddInstaller;
